/**
 * Live Layer API client — thin fetch wrappers around tile-backend's
 * /api/apps/:appId/live-layer/* endpoints. Backend code lives at
 * tile-backend/src/controllers/liveLayerController.ts.
 */

// The Live Layer schema shapes are produced by the AST layer (readers +
// pipeline) and consumed here. They live in `@/ast/contracts` so the AST
// code doesn't have to import `features/`; imported + re-exported here so
// existing feature-side imports (`from '.../live-layer.api'`) keep resolving.
import type {
  LiveLayerFieldType,
  LiveLayerField,
  LiveLayerGroup,
} from '@/ast/contracts/live-layer.types';
import { normalizeThemeRefs } from '@/features/editor/theme-links';

export type { LiveLayerFieldType, LiveLayerField, LiveLayerGroup };

export interface LiveLayerCurrent {
  appId: string;
  env: string;
  category: string;
  version: number;
  config: Record<string, unknown>;
  published: boolean;
  manifestUrl?: string;
  blobUrl?: string;
}

export type EnvKind = 'dev' | 'prod';

/**
 * A named, switchable snapshot of the app — content plus the code that goes
 * with it. Exactly one is `isMain` (the baseline every window returns to) and
 * exactly one is `isLive` (what the store is serving).
 */
export interface AppVersion {
  id: string;
  appId: string;
  env: EnvKind;
  name: string;
  isMain: boolean;
  isLive: boolean;
  saveId: string | null;
  status: 'draft' | 'ready' | 'archived';
  note: string | null;
  layerCount?: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * A window during which one version owns the store: `[startsAt, endsAt)`.
 * At the end the store goes back to main — but only if this window's version
 * is still the one live, so a manual switch mid-window is never yanked away.
 */
export interface VersionWindow {
  id: string;
  appId: string;
  env: EnvKind;
  variantId: string;
  variantName: string | null;
  startsAt: string;
  endsAt: string;
  timezone: string;
  status: 'pending' | 'active' | 'completed' | 'cancelled' | 'failed';
  activatedAt: string | null;
  revertedAt: string | null;
  activationPushId: string | null;
  revertPushId: string | null;
  attempts: number;
  lastError: string | null;
  note: string | null;
  createdBy: string;
  createdAt: string;
}

/**
 * A failed Live Layer request. `message` is the server's own sentence, ready to
 * show a user; `status` and `body` are there for callers that need to react to
 * a specific case (a 409 wanting the schedule that's already queued, say).
 */
export interface LiveLayerApiError extends Error {
  status?: number;
  body?: any;
}

async function call<T>(input: string, init?: RequestInit): Promise<T> {
  // A ceiling on every LL request. The Workspace/Panel fire several
  // requests in parallel when env or cats change; without a hard
  // timeout a stuck connection bubbles up as a generic "Operation timed
  // out" overlay because the underlying socket layer's keepalive trips
  // long before the fetch ever resolves.
  //
  // READS get 12s: they should be quick, and a stalled one is worth saying so
  // early. WRITES get much longer, because they legitimately are not quick —
  // activating a version publishes a config, rebuilds the CDN manifest and can
  // start an OTA push; cancelling a running window does the same work in
  // reverse. Holding those to a read's budget aborts the fetch while the
  // server carries on and finishes the job, so the user is told their action
  // failed when it did not, and the obvious next move is to do it again.
  //
  // The budget also has to cover TIME SPENT QUEUED in the browser, not just
  // the server's work: the timer starts here, and the editor keeps enough
  // requests in flight to sit behind the per-origin connection limit.
  const method = (init?.method || 'GET').toUpperCase();
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), method === 'GET' ? 12_000 : 45_000);
  try {
    const res = await fetch(input, {
      credentials: 'include',
      ...init,
      signal: init?.signal || ctrl.signal,
    });
    if (!res.ok) {
      // The message must be showable as-is: these surface in modals and
      // banners, and an earlier version threw `${url} → ${status}: ${rawBody}`,
      // which put a request path and a wall of JSON in front of a merchant who
      // has no idea what either means. Every endpoint here answers with
      // `{ error: '<sentence>' }`, so use that and keep the machine-readable
      // parts on the error object for callers that want them.
      const raw = await res.text();
      let parsed: any;
      try {
        parsed = JSON.parse(raw);
      } catch {
        /* not JSON — a proxy error page or an empty body */
      }
      const err: LiveLayerApiError = new Error(
        parsed?.error || 'Something went wrong. Please try again.',
      );
      err.status = res.status;
      err.body = parsed;
      throw err;
    }
    return (await res.json()) as T;
  } catch (err: any) {
    if (err?.name === 'AbortError') {
      throw new Error('That took too long. Check your connection and try again.');
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Every published config crosses this module, in both directions, which makes it
 * the one place a stored theme link can be migrated.
 *
 * A link used to be stored the way the SOURCE writer stores it — the call text
 * `getThemeValue("colors.primary")`. As data that is inert: nothing evaluates a
 * published JSON string, so it reached the device verbatim and an app's lookup,
 * splitting on the first `.`, read its group as `getThemeValue("colors` and
 * passed the whole thing through into a style. The value is a bare token path
 * now; see `@/features/editor/theme-links`.
 *
 * Normalising on the way OUT migrates a payload the next time its panel
 * publishes, so no separate sweep is needed. Normalising on the way IN means the
 * editor, the preview and the dirty comparison all work in one form even before
 * that publish happens — the two forms denote the same link, so collapsing them
 * cannot mask a real edit.
 */
const migrateConfig = <T extends { config?: Record<string, unknown> }>(res: T): T =>
  res && res.config ? { ...res, config: normalizeThemeRefs(res.config) } : res;

export const liveLayerApi = {
  getSchema: (appId: string) =>
    call<{ groups: LiveLayerGroup[]; source: string | null; note?: string }>(
      `/api/apps/${encodeURIComponent(appId)}/live-layer/schema`,
    ),

  /** One-click adoption — adds live-layer.schema.json to the latest save. */
  enable: (appId: string) =>
    call<{ saveId: string; schemaPath: string; note?: string }>(
      `/api/apps/${encodeURIComponent(appId)}/live-layer/enable`,
      { method: 'POST' },
    ),

  /**
   * `variantId` reads that VERSION's config rather than what the store is
   * serving — which is how the editor opens a campaign that is waiting for its
   * window. Omit it for the ordinary "show me what's live" case.
   */
  getCurrent: (appId: string, env: EnvKind, category: string, variantId?: string) =>
    call<LiveLayerCurrent>(
      `/api/apps/${encodeURIComponent(appId)}/live-layer/${env}?category=${encodeURIComponent(
        category,
      )}${variantId ? `&variantId=${encodeURIComponent(variantId)}` : ''}`,
    ).then(migrateConfig),

  listLayers: (appId: string, env: EnvKind) =>
    call<{
      layers: { category: string; version: number; updatedBy: string; updatedAt: string }[];
    }>(`/api/apps/${encodeURIComponent(appId)}/live-layer/${env}/layers`),

  /**
   * `variantId` publishes INTO that version instead of the store. The edit is
   * recorded and the CDN is left alone — `wentLive: false` comes back to say
   * so. That is what lets a campaign be built ahead of its window instead of
   * having to be made live to be edited.
   */
  publish: (
    appId: string,
    env: EnvKind,
    body: {
      config: Record<string, unknown>;
      category?: string;
      baseVersion?: number;
      note?: string;
      variantId?: string;
    },
  ) =>
    call<LiveLayerCurrent>(`/api/apps/${encodeURIComponent(appId)}/live-layer/${env}/publish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, config: normalizeThemeRefs(body.config) }),
    }).then(migrateConfig),

  listVersions: (appId: string, env: EnvKind, category: string, limit = 50) =>
    call<{
      category: string;
      versions: {
        id: string;
        version: number;
        note: string | null;
        createdBy: string;
        createdAt: string;
        sizeBytes: number;
      }[];
    }>(
      `/api/apps/${encodeURIComponent(
        appId,
      )}/live-layer/${env}/versions?category=${encodeURIComponent(category)}&limit=${limit}`,
    ),

  rollback: (appId: string, env: EnvKind, toVersion: number, category: string, note?: string) =>
    call<LiveLayerCurrent>(`/api/apps/${encodeURIComponent(appId)}/live-layer/${env}/rollback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ toVersion, category, note }),
    }),

  deleteLayer: (appId: string, env: EnvKind, category: string) =>
    call<{ deleted: true }>(
      `/api/apps/${encodeURIComponent(
        appId,
      )}/live-layer/${env}/layers?category=${encodeURIComponent(category)}`,
      { method: 'DELETE' },
    ),

  /** Per-app taxonomy { cat1: cat2[] }. Drives the Panel's dropdowns. */
  getTaxonomy: (appId: string) =>
    call<{ taxonomy: Record<string, string[]> }>(
      `/api/apps/${encodeURIComponent(appId)}/live-layer/taxonomy`,
    ),

  setTaxonomy: (appId: string, taxonomy: Record<string, string[]>) =>
    call<{ taxonomy: Record<string, string[]> }>(
      `/api/apps/${encodeURIComponent(appId)}/live-layer/taxonomy`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taxonomy }),
      },
    ),
};

/**
 * Versions and their scheduled windows.
 *
 * Kept as its own object rather than folded into `liveLayerApi` because a
 * version is not a Live Layer concept — it wraps content AND code, and
 * activating one starts a Cloud Push. Backend:
 * tile-backend/src/controllers/appVariantController.ts and
 * appVariantScheduleController.ts.
 */
/**
 * ONE NAMING SEAM, HERE, ON PURPOSE.
 *
 * The database calls these `app_variants` and the routes are `/variants/*`;
 * everything a user reads calls them **versions**. Rather than let both words
 * loose in the UI, the wire's `variants` / `schedules` keys are unwrapped into
 * `versions` / `windows` at this boundary and nowhere else. Renaming the
 * backend instead would have meant a migration and a route change for a
 * vocabulary choice.
 */
export const versionsApi = {
  /**
   * Lists versions, creating `Main` on the way through if the app has never
   * had one — so the first call on an existing app returns a sensible baseline
   * rather than an empty list.
   */
  list: (appId: string, env: EnvKind, includeArchived = false) =>
    call<{ variants: AppVersion[] }>(
      `/api/apps/${encodeURIComponent(appId)}/variants/${env}${
        includeArchived ? '?includeArchived=true' : ''
      }`,
    ).then((r) => ({ versions: r.variants })),

  create: (appId: string, env: EnvKind, body: { name: string; forkFrom?: string; note?: string }) =>
    call<{ variant: AppVersion }>(`/api/apps/${encodeURIComponent(appId)}/variants/${env}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => ({ version: r.variant })),

  /**
   * Switch the store to this version, now. Moves the content pointers and
   * starts a Cloud Push of its code — `pushError` is reported rather than
   * thrown, because the content half has already landed and is already
   * correct.
   *
   * The push only happens when the code actually changes. `pushSkipped` says
   * why it did not: the version ships no code of its own, or its code is
   * already what devices are running (going back to a version, ending a
   * campaign — the previous bundle is still the right one). So `pushId: null`
   * with no `pushError` means nothing needed shipping, not that shipping failed.
   */
  activate: (appId: string, env: EnvKind, id: string) =>
    call<{
      variant: AppVersion;
      categoriesSwitched: string[];
      categoriesUncovered: string[];
      pushId: string | null;
      pushError: string | null;
      pushSkipped: 'config-only' | 'code-unchanged' | null;
    }>(
      `/api/apps/${encodeURIComponent(appId)}/variants/${env}/${encodeURIComponent(id)}/activate`,
      { method: 'POST' },
    ).then((r) => ({ ...r, version: r.variant })),

  rename: (appId: string, env: EnvKind, id: string, body: { name?: string; note?: string }) =>
    call<{ variant: AppVersion }>(
      `/api/apps/${encodeURIComponent(appId)}/variants/${env}/${encodeURIComponent(id)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    ).then((r) => ({ version: r.variant })),

  /**
   * Point a version at a specific save — how local work gets parked with the
   * version it was done on when the editor switches away.
   */
  setSave: (appId: string, env: EnvKind, id: string, saveId: string) =>
    call<{ variant: AppVersion }>(
      `/api/apps/${encodeURIComponent(appId)}/variants/${env}/${encodeURIComponent(id)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ saveId }),
      },
    ).then((r) => ({ version: r.variant })),

  archive: (appId: string, env: EnvKind, id: string) =>
    call<{ variant: AppVersion }>(
      `/api/apps/${encodeURIComponent(appId)}/variants/${env}/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ).then((r) => ({ version: r.variant })),

  /**
   * Open windows (pending + active), plus — with `recentHours` — the ones that
   * finished inside that trailing window.
   *
   * The trailing part is not a nicety. Open windows alone answer "what is
   * booked", and a window drops out of that set the moment it completes or
   * FAILS, which is precisely when someone wants to know what happened. Without
   * it the pill just disappears and a failed launch is indistinguishable from a
   * successful one. `includeFinished` is the unbounded form, for history views.
   */
  listWindows: (
    appId: string,
    env: EnvKind,
    opts: { includeFinished?: boolean; recentHours?: number } = {},
  ) => {
    const q = new URLSearchParams();
    if (opts.includeFinished) q.set('includeFinished', 'true');
    if (opts.recentHours) q.set('recentHours', String(opts.recentHours));
    const qs = q.toString();
    return call<{ schedules: VersionWindow[] }>(
      `/api/apps/${encodeURIComponent(appId)}/variant-schedules/${env}${qs ? `?${qs}` : ''}`,
    ).then((r) => ({ windows: r.schedules }));
  },

  /**
   * Book `[startsAt, endsAt)` for a version. Both MUST be absolute instants
   * (ISO with an offset) — the backend rejects a bare local time rather than
   * guess a zone, because guessing wrong launches a campaign at the wrong hour
   * and nobody notices until it has happened.
   *
   * A 409 means the range overlaps an existing window; the error carries the
   * conflicting one on `body.conflict`.
   */
  schedule: (
    appId: string,
    env: EnvKind,
    body: { variantId: string; startsAt: string; endsAt: string; timezone?: string; note?: string },
  ) =>
    call<{ schedule: VersionWindow }>(
      `/api/apps/${encodeURIComponent(appId)}/variant-schedules/${env}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    ).then((r) => ({ window: r.schedule })),

  reschedule: (
    appId: string,
    env: EnvKind,
    id: string,
    body: { startsAt?: string; endsAt?: string; note?: string },
  ) =>
    call<{ schedule: VersionWindow }>(
      `/api/apps/${encodeURIComponent(appId)}/variant-schedules/${env}/${encodeURIComponent(id)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    ).then((r) => ({ window: r.schedule })),

  /**
   * Cancel a window. A pending one simply never runs; cancelling an ACTIVE one
   * ends it immediately and puts main back — `revertedNow` says which happened,
   * because "cancelled" meaning "still live" would be the worst possible
   * reading of the button.
   */
  cancel: (appId: string, env: EnvKind, id: string) =>
    call<{ schedule: VersionWindow; revertedNow: boolean }>(
      `/api/apps/${encodeURIComponent(appId)}/variant-schedules/${env}/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ).then((r) => ({ window: r.schedule, revertedNow: r.revertedNow })),
};
